import {useContext, useMemo} from 'react'

import Context from '@s-ui/react-context'

const DATE_SEPARATOR = '/'
const MILLISECONDS_IN_A_DAY = 86400000
const MILLISECONDS_IN_AN_HOUR = 3600000
const MILLISECONDS_IN_A_MINUTE = 60000

// Server (pods run in UTC) and browser must format the same text, or hydration fails and React
// re-renders the whole page. Every date shown is Spanish local time unless the caller passes another zone.
const DEFAULT_TIME_ZONE = 'Europe/Madrid'

const DIFF_MINUTES_SHOW_NOW = 3
const NOW_OFFSET = MILLISECONDS_IN_A_MINUTE * DIFF_MINUTES_SHOW_NOW

const REPRESENTATION_TYPE = {
  NUMERIC: 'numeric',
  SHORT_NUMERIC: '2-digit',
  LONG_STRING: 'long',
  SHORT_STRING: 'short',
  NARROW: 'narrow'
}

// Calendar day of `date` in `timeZone` (YYYY-MM-DD), so "today" and "yesterday" do not depend on
// the zone of the machine that renders.
function getDayKey(date, timeZone) {
  return date.toLocaleDateString('en-CA', {timeZone})
}

function isValidDate(date) {
  return date.getTime() > 0
}

function getNumericShortFormat({date, locale, timeZone}) {
  const formattedDate = date.toLocaleDateString(locale, {
    year: REPRESENTATION_TYPE.NUMERIC,
    month: REPRESENTATION_TYPE.NUMERIC,
    day: REPRESENTATION_TYPE.NUMERIC,
    timeZone
  })

  const numericShortFormatArray = formattedDate.split(DATE_SEPARATOR)
  const {0: firstGroup, 1: secondGroup, 2: year} = numericShortFormatArray
  const firstGroupTwoDigits = ('0' + firstGroup).slice(-2)
  const lastGroupTwoDigits = ('0' + secondGroup).slice(-2)

  const numericShortFormat = [firstGroupTwoDigits, lastGroupTwoDigits].join(DATE_SEPARATOR)
  const numericFormat = [numericShortFormat, year].join(DATE_SEPARATOR)

  return {
    numericFormat,
    numericShortFormat
  }
}

function getRelativeFormat({date, i18n, hour, timeZone}) {
  const nowDate = new Date()

  const millisecondsSinceLastModification = nowDate - date

  const diffMinutes = Math.round((millisecondsSinceLastModification % MILLISECONDS_IN_A_DAY) / MILLISECONDS_IN_A_MINUTE)

  const isNow = millisecondsSinceLastModification < NOW_OFFSET
  if (isNow) return i18n.t('COMMON_NOW')

  const isLastHour = millisecondsSinceLastModification < MILLISECONDS_IN_AN_HOUR
  if (isLastHour)
    return i18n.t('COMMON_MINUTES_AGO', {
      minutes: diffMinutes
    })

  const dayKey = getDayKey(date, timeZone)

  const isToday = dayKey === getDayKey(nowDate, timeZone)
  if (isToday) return `${i18n.t('COMMON_TODAY')} ${hour}`

  const isYesterday = dayKey === getDayKey(new Date(nowDate.getTime() - MILLISECONDS_IN_A_DAY), timeZone)
  if (isYesterday) return `${i18n.t('COMMON_YESTERDAY')} ${hour}`
}

const getDateFormatsWith =
  ({locale, i18n, timeZone}) =>
  (date, locale) => {
    if (!isValidDate(date)) return {}
    const dayFormat = date.toLocaleDateString(locale, {
      day: REPRESENTATION_TYPE.NUMERIC,
      timeZone
    })

    const descriptiveFormat = date.toLocaleDateString(locale, {
      weekday: REPRESENTATION_TYPE.LONG_STRING,
      year: REPRESENTATION_TYPE.NUMERIC,
      month: REPRESENTATION_TYPE.LONG_STRING,
      day: REPRESENTATION_TYPE.NUMERIC,
      timeZone
    })

    const descriptiveShortFormat = date.toLocaleDateString(locale, {
      year: REPRESENTATION_TYPE.NUMERIC,
      month: REPRESENTATION_TYPE.LONG_STRING,
      day: REPRESENTATION_TYPE.NUMERIC,
      timeZone
    })

    const descriptiveDayMonthFormat = date.toLocaleDateString(locale, {
      month: REPRESENTATION_TYPE.LONG_STRING,
      day: REPRESENTATION_TYPE.NUMERIC,
      timeZone
    })

    const descriptiveDayMonthExtendedFormat = date.toLocaleDateString(locale, {
      weekday: REPRESENTATION_TYPE.LONG_STRING,
      month: REPRESENTATION_TYPE.LONG_STRING,
      day: REPRESENTATION_TYPE.NUMERIC,
      timeZone
    })

    const hourFormat = date.toLocaleString(locale, {
      hour: REPRESENTATION_TYPE.SHORT_NUMERIC,
      minute: REPRESENTATION_TYPE.SHORT_NUMERIC,
      timeZone
    })

    const hourShortFormat = date.toLocaleString(locale, {
      hour: REPRESENTATION_TYPE.NUMERIC,
      minute: REPRESENTATION_TYPE.SHORT_NUMERIC,
      timeZone
    })

    const monthFormat = date.toLocaleDateString(locale, {
      month: REPRESENTATION_TYPE.LONG_STRING,
      timeZone
    })

    const monthShortFormat = date.toLocaleDateString(locale, {
      month: REPRESENTATION_TYPE.SHORT_STRING,
      timeZone
    })

    const yearFormat = date.toLocaleDateString(locale, {
      year: REPRESENTATION_TYPE.NUMERIC,
      timeZone
    })

    const yearShortFormat = date.toLocaleDateString(locale, {
      year: REPRESENTATION_TYPE.SHORT_NUMERIC,
      timeZone
    })

    const {numericFormat, numericShortFormat} = getNumericShortFormat({
      locale,
      date,
      timeZone
    })

    const relativeFormat = getRelativeFormat({date, i18n, hour: hourFormat, timeZone})
    const defaultRelativeFormat = `${numericShortFormat} ${hourFormat}`

    return {
      day: dayFormat,
      descriptive: descriptiveFormat,
      descriptiveShort: descriptiveShortFormat,
      descriptiveDayMonth: descriptiveDayMonthFormat,
      descriptiveDayMonthExtended: descriptiveDayMonthExtendedFormat,
      hour: hourFormat,
      hourShort: hourShortFormat,
      ISO: date.toISOString(),
      month: monthFormat,
      monthShort: monthShortFormat,
      numeric: numericFormat,
      numericShort: numericShortFormat,
      relative: relativeFormat || defaultRelativeFormat,
      timestamp: date.getTime(),
      year: yearFormat,
      yearShort: yearShortFormat
    }
  }

function useDateFormat(dateToFormat, timeZone = DEFAULT_TIME_ZONE) {
  const date = new Date(dateToFormat)
  const {i18n} = useContext(Context)

  const formats = useMemo(() => {
    const {culture: locale} = i18n
    const getDateFormats = getDateFormatsWith({i18n, locale, timeZone})
    return getDateFormats(date, locale)
  }, [date, i18n])

  return formats
}

export {useDateFormat, getDateFormatsWith}
